import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Info, Layers, Pencil, Plus, Star } from "lucide-react";
import { listProducts } from "@/services/products";
import { PG, SRC_TAG, SRC_TAG_CLS } from "@/pages/pricing/priceGridStyle";   // PRICEUNIFY-2 · 与售价总览共用表格样式
import { getOptionCardPrices, type CardPriceOption, type CellSource } from "@/services/pricing";
import {
  activeMappingsByPlatform, optionQueryKey, overriddenCostItems, productOptionsApi,
  type CreateOptionBody, type ProductOption,
} from "@/services/productOptions";
import type { ProductWithLegs } from "@/services/types/products";
import { GUIDE_LANGUAGE_LABEL } from "@/services/types/products";
import { useCan } from "@/auth/permissions";
import { LoadingState } from "@/shared/ui/PageHeader";
import { englishNameError } from "./productNames";

/**
 * OPT-C1 · 产品详情「选项」页签（样稿 ① + ②）。
 * 今日版：选项共用产品的团期 / 车 / 人数上限，卡片上这些显示「跟随产品」的值；不显示价格；只有 1 个选项也照常显示。
 */

const PLATFORM_LABEL: Record<string, string> = { gyg: "GYG", viator: "Viator", bokun: "Bokun", klook: "Klook" };
const FIXED_PLATFORMS = ["gyg", "viator"];

export const apiErrorText = (e: unknown, fallback: string): string => {
  const d = (e as { detail?: unknown })?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object" && "message" in d) return String((d as { message: unknown }).message);
  const m = (e as Error)?.message;
  return m && m !== "[object Object]" ? m : fallback;
};

function Pill({ tone, children, ...rest }: { tone: "blue" | "green" | "gray" | "violet" | "amber"; children: React.ReactNode } & React.HTMLAttributes<HTMLSpanElement>) {
  const cls = {
    blue: "bg-blue-50 text-blue-700", green: "bg-emerald-50 text-emerald-700", gray: "bg-slate-100 text-slate-500",
    violet: "bg-violet-50 text-violet-700", amber: "bg-amber-50 text-amber-700",
  }[tone];
  return <span {...rest} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}>{children}</span>;
}

/** OPT-C2 · 单独设过的字段标「本选项」；跟随的不标（卡片显示 effective 值） */
const Own = () => <span className="ml-1 rounded bg-violet-50 px-1 text-[10.5px] font-semibold text-violet-600" data-testid="own-tag">本选项</span>;
const hhmmTo12h = (t: string) => { const m = /^(\d{2}):(\d{2})$/.exec(t); if (!m) return t; const h = parseInt(m[1], 10); return `${h % 12 || 12}:${m[2]} ${h >= 12 ? "PM" : "AM"}`; };

const KV = ({ k, children }: { k: string; children: React.ReactNode }) => (
  <div><dt className="text-[11.5px] text-slate-400">{k}</dt><dd className="mt-px text-[13px] font-medium text-slate-800">{children}</dd></div>
);

const ROW_LABEL: Record<string, string> = { b2b_settle: "同行结算", b2b_list: "同行建议", direct: "散客 / 手机站", social: "社媒", api: "API 平台净价" };
const ROW_ORDER = ["b2b_settle", "b2b_list", "direct", "social", "api"];
const SRC_LABEL: Record<CellSource, string> = { fixed: "固定", auto: "", own: "固定", follow: "跟第一个" };

/** PRICEUNIFY-1 · 选项卡片上的 5 渠道价（整个产品一次批量接口取，不是每张卡各打一次）。一日游 / 门票一个价（门票成人 / 儿童两列）；多日游显示 2 人/间，鼠标放上去看 1–4 人/间。 */
function CardPrices({ prices, multi, isTicket, isDefault }: { prices: CardPriceOption; multi: boolean; isTicket: boolean; isDefault: boolean }) {
  const pick = (cells: Record<string, { cents: number; source: CellSource }> | undefined) => (multi ? cells?.["2"] : cells?.all);
  const tiers = (cells: Record<string, { cents: number; source: CellSource }> | undefined) => ["1", "2", "3", "4"].map((k) => `${k} 人/间 ${usd2(cells?.[k]?.cents)}`).join(" · ");
  const hasChild = isTicket && ROW_ORDER.some((k) => prices.rows[k]?.child);
  if (multi) {   // PRICEUNIFY-2 · 多日游：直接显示 渠道 × 1–4 人/间 全部价 + 来源（和售价总览同一套表格样式；窄屏横向滚动，不换行堆叠）
    return (
      <div className={PG.wrap} data-testid="option-prices">
        <table className={`${PG.table} min-w-[460px]`} data-testid="option-prices-table">
          <thead className={PG.thead}>
            <tr><th className={PG.th}>渠道价 / 人</th>{[1, 2, 3, 4].map((n) => <th key={n} className={PG.th}>{n} 人/间</th>)}{!isDefault && <th className={PG.th}>来源</th>}</tr>
          </thead>
          <tbody>
            {ROW_ORDER.map((k) => {
              const cells = prices.rows[k];
              if (!cells || !Object.keys(cells).length) return null;
              const src = cells["2"]?.source ?? "follow";
              return (
                <tr key={k} className={PG.tr} data-testid={`option-price-${k}`}>
                  <td className={`${PG.td} whitespace-nowrap text-slate-500`}>{ROW_LABEL[k]}</td>
                  {["1", "2", "3", "4"].map((t) => <td key={t} className={`${PG.td} font-semibold text-slate-900`} data-testid={`option-price-${k}-${t}`}>{usd2(cells[t]?.cents)}</td>)}
                  {!isDefault && <td className={PG.td}><span className={`${SRC_TAG_CLS} ${src === "own" ? SRC_TAG.own.cls : SRC_TAG.follow.cls}`} data-testid={`option-price-src-${k}`}>{SRC_LABEL[src] || ""}</span></td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2" data-testid="option-prices">
      <div className="mb-1 flex text-[11px] text-slate-400"><span className="flex-1">渠道价 / 人</span><span className="w-[92px] text-right">{hasChild ? "成人" : multi ? "2 人/间" : "价格"}</span>{hasChild && <span className="w-[80px] text-right">儿童</span>}{!isDefault && <span className="w-[64px] text-right">来源</span>}</div>
      {ROW_ORDER.map((k) => {
        const cells = prices.rows[k];
        const c = pick(cells);
        if (!c) return null;
        const kid = cells?.child;
        return (
          <div key={k} className="flex items-center py-px text-[12.5px]" data-testid={`option-price-${k}`} title={multi ? tiers(cells) : undefined}>
            <span className="flex-1 text-slate-500">{ROW_LABEL[k]}</span>
            <b className="w-[92px] text-right tabular-nums text-slate-900">{usd2(c.cents)}</b>
            {hasChild && <b className="w-[80px] text-right tabular-nums text-slate-900">{kid ? usd2(kid.cents) : "—"}</b>}
            {!isDefault && <span className="w-[64px] text-right text-[11px]"><span className={`rounded-full px-1.5 py-px ${c.source === "own" ? "bg-blue-50 text-blue-600" : "border border-slate-200 text-slate-500"}`} data-testid={`option-price-src-${k}`}>{SRC_LABEL[c.source] || ""}</span></span>}
          </div>
        );
      })}
    </div>
  );
}

function OptionCard({ o, product, canEdit, onEdit, onCopy, prices }: { o: ProductOption; product: ProductWithLegs; canEdit: boolean; onEdit: () => void; onCopy: () => void; prices?: { multi: boolean; opt: CardPriceOption } | null }) {
  const eff = o.effective;
  const langs = (eff.guide_languages ?? []).map((l) => GUIDE_LANGUAGE_LABEL[l] ?? l).join("、") || "—";
  const costOv = overriddenCostItems(o);
  const connected = activeMappingsByPlatform(o.mappings);
  const platforms = [...new Set([...FIXED_PLATFORMS, ...Object.keys(connected)])];
  const groupType = product.is_small_group ? "小团" : "拼团";
  const isTicket = product.category === "ticket";   // TICKETOPT-1 · 门票选项卡片：关联票种 / 入场时间 / 截单 / 每单人数；没有团型 / 上车点 / 车
  return (
    <div className={`flex min-w-0 flex-col gap-2.5 rounded-xl border bg-white p-4 ${o.is_default ? "border-blue-300 shadow-[0_0_0_3px_rgba(37,99,235,0.08)]" : "border-slate-200"}`} data-testid="option-card" data-option-id={o.id}>
      <div className="flex flex-wrap items-start gap-2">
        <b className="text-[15px] text-slate-900">{o.name_cn}</b>
        {o.is_default && <Pill tone="blue" data-testid="option-default-pill"><Star size={11} />默认</Pill>}
        {o.status === "active" ? <Pill tone="green">可售</Pill> : <Pill tone="gray" data-testid="option-inactive-pill">停售</Pill>}
        <span className="font-mono text-[11.5px] text-slate-500">{o.code}</span>
        <div className="ml-auto flex flex-wrap gap-1.5">
          {canEdit && (
            <button type="button" onClick={onEdit} data-testid="option-edit" className="inline-flex h-7 items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 text-[12px] font-medium text-slate-700 hover:bg-slate-50"><Pencil size={12} />编辑</button>
          )}
          {!canEdit && (
            <button type="button" onClick={onEdit} data-testid="option-view" className="inline-flex h-7 items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 text-[12px] font-medium text-slate-700 hover:bg-slate-50">查看</button>
          )}
          {canEdit && !isTicket && (
            <button type="button" onClick={onCopy} data-testid="option-copy" className="inline-flex h-7 items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 text-[12px] font-medium text-slate-700 hover:bg-slate-50"><Copy size={12} />复制为新选项</button>
          )}
        </div>
      </div>
      {o.name_en && <div className="-mt-1.5 text-[12px] text-slate-400">{o.name_en}</div>}
      {isTicket ? (
        <dl className="grid grid-cols-2 gap-x-3.5 gap-y-2 sm:grid-cols-3" data-testid="ticket-option-facts">
          <KV k="关联票种">{o.ticket_type_name_cn ?? "—"}</KV>
          <KV k="入场时间">{o.overrides.start_time ? hhmmTo12h(o.overrides.start_time as string) : <span className="text-slate-500">不限时段</span>}</KV>
          <KV k="截单">{o.overrides.cutoff_hours != null ? `入场前 ${o.overrides.cutoff_hours} 小时` : <span className="text-slate-500">不额外截单</span>}</KV>
          <KV k="每单人数">{o.overrides.min_pax_per_booking ?? 1} – {o.overrides.max_pax_per_booking ?? "不限"}</KV>
          <KV k="游玩时长">{o.visit_minutes != null ? `${o.visit_minutes} 分钟` : <span className="text-slate-500">未填</span>}</KV>
        </dl>
      ) : (
      <dl className="grid grid-cols-2 gap-x-3.5 gap-y-2 sm:grid-cols-3">
        <KV k="团型 · 每团上限">{groupType} · {eff.max_pax_per_departure ?? product.max_pax_per_departure ?? "—"} 人 {o.overrides.max_pax_per_departure != null ? <Own /> : <span className="text-[11px] font-normal text-slate-400">跟随产品</span>}</KV>
        <KV k="成团人数">{eff.min_pax ?? product.min_pax ?? "—"} 人 {o.overrides.min_pax != null ? <Own /> : <span className="text-[11px] font-normal text-slate-400">跟随产品</span>}</KV>
        <KV k="讲解语言">{langs}{o.overrides.guide_languages == null && <span className="ml-1 text-[11px] font-normal text-slate-400">跟随产品</span>}</KV>
        <KV k="出发时间">{(eff.start_time ?? o.overrides.start_time) ? hhmmTo12h((eff.start_time ?? o.overrides.start_time) as string) : <span className="text-slate-500">跟随产品</span>}{o.overrides.start_time != null && <Own />}{o.overrides.weekly_cadence_json != null && <span className="ml-1 text-[11px] font-normal text-violet-600">· 开团规律<Own /></span>}</KV>
        <KV k="上车点">{(eff.pickup_stops ?? []).length} 个固定上车点{o.overrides.pickup_stops == null && <span className="ml-1 text-[11px] font-normal text-slate-400">跟随产品</span>}</KV>
        <KV k="车 · 成本">
          {(o.overrides.allowed_vehicle_models ?? o.overrides.allowed_vehicle_ids ?? o.overrides.default_vehicle_id) != null
            ? <>{(o.overrides.allowed_vehicle_models ?? []).join("、") || "指定车辆"}<Own /></>
            : <span className="text-slate-500">跟随产品</span>}
          {costOv.length > 0 && <span className="ml-1 text-violet-600" data-testid="option-cost-override">· {costOv.join("、")}单独设</span>}
        </KV>
      </dl>
      )}
      {prices && <CardPrices prices={prices.opt} multi={prices.multi} isTicket={isTicket} isDefault={o.is_default} />}
      <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-2.5">
        {platforms.map((p) => (connected[p]
          ? <Pill key={p} tone="green" data-testid={`option-platform-${p}`}>{PLATFORM_LABEL[p] ?? p} 已连 {connected[p]}</Pill>
          : <Pill key={p} tone="gray" data-testid={`option-platform-${p}`}>{PLATFORM_LABEL[p] ?? p} 未连</Pill>))}
        {o.pricing_summary && o.pricing_summary.mode === "own" && (
          <span data-testid="option-own-pricing" className="inline-flex items-center gap-1.5 text-[11.5px]">
            <span className="rounded-full bg-blue-50 px-2 py-px font-medium text-blue-600">本选项单独定价</span>
            <span className="text-slate-400">{o.pricing_summary.fixed_channels} 个渠道固定 · {o.pricing_summary.follow_channels} 个跟第一个</span>
          </span>
        )}
        <span className="ml-auto text-[11.5px] text-slate-400">订单 {o.order_count} 单</span>
      </div>
    </div>
  );
}

const usd2 = (c: number | null | undefined) => (c == null ? "—" : `$${(c / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

type Way = "scratch" | "option" | "product";

/** 新建选项弹窗（样稿 ②）：三种起点；名称中英必填，英文名走和产品同一套检查；编号保存后生成。 */
export function NewOptionModal({ productId, options, presetFromOptionId, onClose, onCreated }: {
  productId: string; options: ProductOption[]; presetFromOptionId?: string | null; onClose: () => void; onCreated: (o: ProductOption) => void;
}) {
  const qc = useQueryClient();
  const defaultOpt = options.find((o) => o.is_default) ?? options[0];
  const [way, setWay] = useState<Way>(presetFromOptionId ? "option" : "option");
  const [fromOption, setFromOption] = useState<string>(presetFromOptionId ?? defaultOpt?.id ?? "");
  const [productQuery, setProductQuery] = useState("");
  const [fromProduct, setFromProduct] = useState<{ id: string; label: string } | null>(null);
  const [nameCn, setNameCn] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [touchedEn, setTouchedEn] = useState(false);

  const productsQ = useQuery({
    queryKey: ["products", "option-copy-search", productQuery],
    queryFn: () => listProducts({ q: productQuery || undefined, limit: 20 } as never),
    enabled: way === "product",
  });
  const enErr = nameEn.trim() ? englishNameError(nameEn, nameCn) : null;
  const wayOk = way === "scratch" || (way === "option" && !!fromOption) || (way === "product" && !!fromProduct);
  const valid = !!nameCn.trim() && !!nameEn.trim() && !enErr && wayOk;
  const nextLetterHint = (() => {
    const used = new Set(options.map((o) => o.letter));
    for (let c = 65; c <= 90; c++) if (!used.has(String.fromCharCode(c))) return String.fromCharCode(c);
    return null;
  })();

  const create = useMutation({
    mutationFn: () => {
      const body: CreateOptionBody = { name_cn: nameCn.trim(), name_en: nameEn.trim() };
      if (way === "option") body.copy_from_option_id = fromOption;
      if (way === "product" && fromProduct) body.copy_from_product_id = fromProduct.id;
      return productOptionsApi.create(productId, body);
    },
    onSuccess: (o) => { qc.invalidateQueries({ queryKey: optionQueryKey(productId) }); onCreated(o); },
    onError: (e) => setErr(apiErrorText(e, "创建失败")),
  });

  const wayBox = (k: Way, title: string, desc: string, extra?: React.ReactNode) => (
    <div role="radio" aria-checked={way === k} tabIndex={0} onClick={() => setWay(k)} data-testid={`way-${k}`}
      className={`flex cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2.5 ${way === k ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}>
      <span className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-slate-400 ${way === k ? "border-[5px] border-blue-600" : "border-[1.5px]"}`} />
      <div className="min-w-0 flex-1"><b className="text-[13.5px] text-slate-900">{title}</b><p className="mt-0.5 text-[12px] text-slate-500">{desc}</p>{way === k && extra}</div>
    </div>
  );
  const copyList = (
    <div className="mt-2 grid grid-cols-2 gap-x-3.5 gap-y-1.5 text-[12px] text-slate-500">
      {["上车点 / 接送", "讲解语言", "餐标 / 已含门票 / 城际用车"].map((t) => <span key={t}><span className="font-bold text-emerald-600">✓ </span>{t}</span>)}
      {["团期（跟随产品，今日版不分开）", "平台连接（要另连）"].map((t) => <span key={t}><span className="font-bold text-slate-400">✕ </span>{t}</span>)}
    </div>
  );
  const inputCls = "rounded-lg border border-slate-300 px-2.5 py-1.5 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in" onClick={onClose} data-testid="new-option-modal-bg">
      <div role="dialog" aria-label="新建选项" className="max-h-[92vh] w-full max-w-[620px] overflow-y-auto rounded-xl border border-slate-200 bg-white p-5 shadow-xl animate-in zoom-in-95 slide-in-from-bottom-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="mb-3 text-base font-semibold text-slate-900">新建选项</h3>
        <div className="flex flex-col gap-2" role="radiogroup" aria-label="起点">
          {wayBox("scratch", "从头新建", "除了名称，其它都空着，自己逐项填。")}
          {wayBox("option", "用本产品的现有选项做模板", "最常用。选一个选项照抄设置，再改不同的地方。", (
            <>
              <select aria-label="模板选项" data-testid="from-option" value={fromOption} onChange={(e) => setFromOption(e.target.value)} className={`mt-2 w-full max-w-xs ${inputCls}`} onClick={(e) => e.stopPropagation()}>
                {options.map((o) => <option key={o.id} value={o.id}>{o.name_cn}（{o.code}{o.is_default ? " · 默认" : ""}）</option>)}
              </select>
              {copyList}
            </>
          ))}
          {wayBox("product", "从另一个产品复制设置", "把以前单独建的同类产品并进来用。只复制设置，原产品和它的老订单不动，确认没问题后把原产品停售。", (
            <div className="mt-2" onClick={(e) => e.stopPropagation()}>
              <input aria-label="搜索产品" data-testid="product-search" placeholder="搜产品编号 / 名称" value={productQuery} onChange={(e) => setProductQuery(e.target.value)} className={`w-full max-w-xs ${inputCls}`} />
              <div className="mt-1.5 max-h-40 overflow-y-auto rounded-lg border border-slate-200">
                {(productsQ.data ?? []).filter((p) => p.id !== productId).map((p) => (
                  <button key={p.id} type="button" data-testid={`product-pick-${p.id}`} onClick={() => setFromProduct({ id: p.id, label: `${p.code} ${p.name_cn}` })}
                    className={`block w-full px-2.5 py-1.5 text-left text-[12.5px] hover:bg-slate-50 ${fromProduct?.id === p.id ? "bg-blue-50 font-medium text-blue-700" : "text-slate-700"}`}>{p.code} · {p.name_cn}</button>
                ))}
                {productsQ.isLoading && <div className="px-2.5 py-2 text-[12px] text-slate-400">搜索中…</div>}
                {!productsQ.isLoading && (productsQ.data ?? []).filter((p) => p.id !== productId).length === 0 && <div className="px-2.5 py-2 text-[12px] text-slate-400">没有匹配的产品</div>}
              </div>
              {fromProduct && <div className="mt-1 text-[12px] text-blue-700">已选：{fromProduct.label}</div>}
            </div>
          ))}
        </div>
        <div className="mt-3.5 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1"><span className="text-[12px] font-medium text-slate-600">选项中文名 <span className="text-rose-600">*</span></span>
            <input data-testid="new-name-cn" className={inputCls} value={nameCn} onChange={(e) => setNameCn(e.target.value)} placeholder="如 精品小团 10–14 人" /></label>
          <label className="flex flex-col gap-1"><span className="text-[12px] font-medium text-slate-600">选项英文名 <span className="text-rose-600">*</span></span>
            <input data-testid="new-name-en" className={`${inputCls} ${touchedEn && enErr ? "border-rose-400" : ""}`} value={nameEn} onChange={(e) => setNameEn(e.target.value)} onBlur={() => setTouchedEn(true)} placeholder="如 Small Group Tour (Max 14)" />
            {touchedEn && enErr
              ? <span className="text-[11.5px] text-rose-600" data-testid="new-name-en-error">{enErr}</span>
              : <span className="text-[11.5px] text-slate-400">平台上客人看到的就是这个；必须是英文（和产品英文名同一套检查）</span>}
          </label>
        </div>
        <label className="mt-3 flex flex-col gap-1"><span className="text-[12px] font-medium text-slate-600">选项编号</span>
          <input readOnly className="rounded-lg border border-slate-300 bg-slate-50 px-2.5 py-1.5 font-mono text-[13px] text-slate-500" value="保存后生成" aria-label="选项编号" data-testid="new-code" />
          <span className="text-[11.5px] text-slate-400">自动生成，不能改：产品编号 + 字母{nextLetterHint ? `（这次是 -${nextLetterHint}）` : ""}，用过的字母不再用</span></label>
        <p className="mt-3 text-[12px] text-slate-500" data-testid="new-option-price-note">售价：新选项默认「和第一个选项同价」（格子空 = 联动第一个选项）；创建后会直接跳到「售价」页，可以「从第一个选项带入」再改，或只填要不一样的格。</p>
        {err && <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700" data-testid="new-option-error">{err}</div>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-8 rounded-lg border border-slate-200 bg-white px-3 text-[13px] text-slate-700 hover:bg-slate-50">取消</button>
          <button type="button" data-testid="new-option-submit" disabled={!valid || create.isPending} onClick={() => { setErr(null); create.mutate(); }}
            className="h-8 rounded-lg bg-blue-600 px-3 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50">创建并继续设置</button>
        </div>
      </div>
    </div>
  );
}

export function ProductOptionsTab({ product }: { product: ProductWithLegs }) {
  const navigate = useNavigate();
  const canEdit = useCan("products.products.update");
  const optsQ = useQuery({ queryKey: optionQueryKey(product.id), queryFn: () => productOptionsApi.list(product.id) });
  const [modal, setModal] = useState<{ from: string | null } | null>(null);
  const options = optsQ.data ?? [];
  const goEdit = (oid: string, pane?: string) => navigate(`/products/${product.id}/options/${oid}${pane ? `?pane=${pane}` : ""}`);
  // PRICEUNIFY-1 · 5 渠道价一次取完（接口按「价格查看」权限拦：没权限 / 算不出就不显示）
  const canSeePrice = useCan("pricing.policies.list");
  const pricesQ = useQuery({ queryKey: ["products", product.id, "option-card-prices"], enabled: canSeePrice, queryFn: () => getOptionCardPrices(product.id), retry: false });
  const priceOf = (oid: string) => { const d = pricesQ.data; const opt = d?.options?.find((x) => x.option_id === oid); return d && opt && !d.error ? { multi: d.multi, opt } : null; };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="options-tab">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-50 text-blue-600"><Layers size={15} /></span>
        <h3 className="text-[15px] font-semibold text-slate-900">选项</h3>
        <span className="text-[12px] text-slate-400">客人下单时选其中一个；每个选项各自连平台</span>
        {canEdit && product.category !== "ticket" && (   // 门票选项由票种决定，不手工新建
          <button type="button" onClick={() => setModal({ from: null })} data-testid="new-option-btn"
            className="ml-auto inline-flex h-8 items-center gap-1 rounded-lg bg-blue-600 px-3 text-[13px] font-semibold text-white hover:bg-blue-700"><Plus size={14} />新建选项</button>
        )}
      </div>
      <div className="mb-3 flex items-start gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-[12.5px] text-blue-900">
        <Info size={14} className="mt-0.5 shrink-0" />
        <span>介绍、图片、逐日行程、包含 / 不包含在「基础与行程」「内容与上架」里改，所有选项共用。选项标题要格式一致、一眼能分出区别（例：普通团 / 精品小团 10–14 人），加了新选项记得回头看一下原来选项的标题。</span>
      </div>
      {optsQ.isError ? (
        <div className="flex items-center justify-center gap-2 py-8 text-[13px] text-slate-500">选项加载失败 · <button onClick={() => optsQ.refetch()} className="font-medium text-blue-600 hover:underline">重试</button></div>
      ) : optsQ.isLoading ? <LoadingState /> : (
        <div className="grid grid-cols-1 gap-3 min-[900px]:grid-cols-2" data-testid="option-cards">
          {options.map((o) => <OptionCard key={o.id} o={o} product={product} canEdit={canEdit} onEdit={() => goEdit(o.id)} onCopy={() => setModal({ from: o.id })} prices={priceOf(o.id)} />)}
        </div>
      )}
      <p className="mt-2.5 text-[12px] text-slate-400">「设为默认」「停售」在编辑页里；默认选项不能停售（先把别的选项设为默认）。已经有订单的选项不能删，只能停售。</p>
      {modal && <NewOptionModal productId={product.id} options={options} presetFromOptionId={modal.from} onClose={() => setModal(null)} onCreated={(o) => { setModal(null); goEdit(o.id, "pricing"); }} />}
    </section>
  );
}
